import {ActionResponse, QuoteAction, QuoteStatus, UserRoles} from "../types";
import {Button, ButtonGroup} from "react-bootstrap";
import React, {useState} from "react";
import {checkIsAllowed, getQuoteStatusFromAction, handlePostError} from "../util/Helpers";
import {useSelector} from "react-redux";
import {RootState} from "../redux/reduxTypes";
import {getQuotePDF, postQuoteAction} from "../api/dataService";
import {useNavigate} from "react-router-dom";
import {Spinner} from "react-bootstrap";
import {ACTION_IN_FLIGHT_MESSAGE, beginAction, endAction, UNKNOWN_ACTION_FAILURE} from "../util/ActionGuard";
import ActionErrorModal from "./ActionErrorModal";

interface QuoteActionButtonProps {
    quote_id: string,
    quote_status: QuoteStatus,
}
const QuoteActionButtons: React.FC<QuoteActionButtonProps> = (props: QuoteActionButtonProps) => {
    const [errorMessages, setErrorMessages] = useState<string[]>([])
    const [isLoading, setLoading] = useState<boolean>(false)
    const user = useSelector((state: RootState) => state.user)
    const userGroups = user.groups
    const navigate = useNavigate()


    const handleQuoteAction = async (quote_ID: string, quoteAction: QuoteAction) => {
        if(quoteAction === QuoteAction.save) {
            try {
                await getQuotePDF(quote_ID)
            } catch (error) {
                setErrorMessages(handlePostError(error, 'QuoteAction'))
            }
            return
        }

        // Survives the remount that paging away and back causes, unlike isLoading
        if(!beginAction(quote_ID, quoteAction)) {
            setErrorMessages([ACTION_IN_FLIGHT_MESSAGE])
            return
        }

        try {
            setLoading(true)

            const response = await postQuoteAction(quote_ID,quoteAction)

            let QuoteActionResponse = response as ActionResponse

            if (QuoteActionResponse && QuoteActionResponse.success) {

                navigate(`/quotes/${getQuoteStatusFromAction(quoteAction) ? getQuoteStatusFromAction(quoteAction) : QuoteStatus.none}`)

            } else {
                // The server refused the change and explains why, say so instead of
                // leaving the spinner running with no feedback
                setErrorMessages([QuoteActionResponse?.message || UNKNOWN_ACTION_FAILURE])
            }
        } catch (error) {
            const messages = handlePostError(error, 'QuoteAction')
            setErrorMessages(messages.length ? messages : [UNKNOWN_ACTION_FAILURE])
        } finally {
            setLoading(false)
            endAction(quote_ID, quoteAction)
        }
    }

    let buttonsToRender;
    switch (props.quote_status) {
        case QuoteStatus.pushed:
            if(checkIsAllowed(userGroups,[UserRoles.management, UserRoles.ownership, UserRoles.sales, UserRoles.officeAdmin])) {
                buttonsToRender = (
                    <div className="d-flex justify-content-center">
                        <ButtonGroup className="btn-group-sm">
                            <Button onClick={() => handleQuoteAction(props.quote_id, QuoteAction.submit)}
                                    className="btn-success"
                                    disabled={isLoading}>Submit</Button>
                        </ButtonGroup>
                        <Button onClick={() => handleQuoteAction(props.quote_id, QuoteAction.reject)}
                                className="btn-sm btn-danger"
                                disabled={isLoading}>Reject</Button>

                    </div>
                );
            } else {
                buttonsToRender = (<div className="d-flex justify-content-center">

                        <ButtonGroup className=" btn-group-sm">
                            <Button
                                onClick={() => handleQuoteAction(props.quote_id, QuoteAction.save)}
                                className="btn-primary"
                                style={{ whiteSpace: 'nowrap' }}
                            >
                                Download PDF
                            </Button>
                        </ButtonGroup>
                    </div>
                )
            }
            break;
        case QuoteStatus.revision:
            if(checkIsAllowed(userGroups,[UserRoles.sales,UserRoles.management, UserRoles.ownership, UserRoles.officeAdmin])){
                buttonsToRender = (
                    <div className="d-flex justify-content-center">
                        <ButtonGroup className="btn-group-sm">
                            <Button className="btn-success"
                                    onClick={() => handleQuoteAction(props.quote_id, QuoteAction.submit)}
                                    disabled={isLoading}>Submit
                            </Button>
                        </ButtonGroup>
                        <Button onClick={() => handleQuoteAction(props.quote_id, QuoteAction.reject)}
                                className="btn-sm btn-danger"
                                disabled={isLoading}>Reject</Button>

                    </div>
                )
            } else {
                buttonsToRender = (<div className="d-flex justify-content-center">
                        <ButtonGroup className=" btn-group-sm">
                            <Button
                                onClick={() => handleQuoteAction(props.quote_id, QuoteAction.save)}
                                className="btn-primary"
                                style={{ whiteSpace: 'nowrap' }}
                            >
                                Download PDF
                            </Button>
                        </ButtonGroup>
                    </div>
                )
            }
            break;
        case QuoteStatus.opened:
            if (checkIsAllowed(userGroups, [UserRoles.sales,UserRoles.management,UserRoles.ownership, UserRoles.officeAdmin])) {
                buttonsToRender = (
                    <div className="d-flex justify-content-center">
                        <ButtonGroup className="btn-group-sm">
                            <Button className="btn-primary"
                                    onClick={() => handleQuoteAction(props.quote_id,QuoteAction.push)}
                                    disabled={isLoading}>Push</Button>
                        </ButtonGroup>
                    </div>
                )
            } else {
                buttonsToRender = (<div className="d-flex justify-content-center">

                        <ButtonGroup className=" btn-group-sm">
                            <Button
                                onClick={() => handleQuoteAction(props.quote_id, QuoteAction.save)}
                                className="btn-primary"
                                style={{ whiteSpace: 'nowrap' }}
                            >
                                Download PDF
                            </Button>
                        </ButtonGroup>
                    </div>
                )
            }
            break;
        case QuoteStatus.submitted:
            if(checkIsAllowed(userGroups,[UserRoles.sales, UserRoles.ownership,UserRoles.management, UserRoles.officeAdmin])) {
                buttonsToRender = (
                    <div className="d-flex flex-column justify-content-center">
                        <ButtonGroup className=" btn-group-sm">
                            <Button className="btn-success"
                                    onClick={() => handleQuoteAction(props.quote_id,QuoteAction.accept)}
                                    disabled={isLoading}>Accept</Button>
                            <Button onClick={() => handleQuoteAction(props.quote_id, QuoteAction.reject)}
                                    className="btn-sm btn-danger"
                                    disabled={isLoading}>Reject</Button>
                        </ButtonGroup>
                        <ButtonGroup className="btn-group-sm p-2">
                            <Button
                                onClick={() => handleQuoteAction(props.quote_id, QuoteAction.save)}
                                className="btn-primary"
                                style={{ whiteSpace: 'nowrap' }}
                            >
                                Download PDF
                            </Button>
                        </ButtonGroup>

                    </div>
                )
            } else if (checkIsAllowed(userGroups,[UserRoles.sales])) {
                buttonsToRender = (<div className="d-flex justify-content-center">
                        <ButtonGroup className=" btn-group-sm">
                            <Button onClick={() => handleQuoteAction(props.quote_id, QuoteAction.reject)}
                                    className="btn-sm btn-danger"
                                    disabled={isLoading}>Reject</Button>
                            <Button
                                onClick={() => handleQuoteAction(props.quote_id, QuoteAction.save)}
                                className="btn-primary"
                                style={{ whiteSpace: 'nowrap' }}
                            >
                                Download PDF
                            </Button>
                        </ButtonGroup>
                    </div>
                )
            } else {
                buttonsToRender = (<div className="d-flex justify-content-center">
                    <ButtonGroup className=" btn-group-sm">
                        <Button
                            onClick={() => handleQuoteAction(props.quote_id, QuoteAction.save)}
                            className="btn-primary"
                            style={{whiteSpace: 'nowrap'}}
                        >
                            Download PDF
                        </Button>
                    </ButtonGroup>
                </div>)
            }
            break;
        case QuoteStatus.accepted:
            if (checkIsAllowed(userGroups, [UserRoles.ownership, UserRoles.management])) {
                buttonsToRender = (
                    <div className="d-flex justify-content-center">
                        <ButtonGroup className=" btn-group-sm">
                            <Button
                                onClick={() => handleQuoteAction(props.quote_id, QuoteAction.save)}
                                className="btn-primary"
                                style={{whiteSpace: 'nowrap'}}
                            >
                                Download PDF
                            </Button>
                        </ButtonGroup>
                    </div>
                )
            } else {
                buttonsToRender = (<div className="d-flex justify-content-center">

                        <ButtonGroup className=" btn-group-sm">
                            <Button
                                onClick={() => handleQuoteAction(props.quote_id, QuoteAction.save)}
                                className="btn-primary"
                                style={{ whiteSpace: 'nowrap' }}
                            >
                                Download PDF
                            </Button>
                        </ButtonGroup>
                    </div>
                )
            }

            break;
        case QuoteStatus.rejected:
            if(checkIsAllowed(userGroups,[UserRoles.ownership,UserRoles.management, UserRoles.sales])) {
                buttonsToRender = (
                    <div className="d-flex justify-content-center">
                        <ButtonGroup className=" btn-group-sm">
                            {/*TODO: */}
                            <Button
                                onClick={() => handleQuoteAction(props.quote_id, QuoteAction.re_open)}
                                className="btn-warning"
                                style={{ whiteSpace: 'nowrap' }}
                                disabled={isLoading}>
                                 Re-Open
                            </Button>
                            <Button
                                onClick={() => handleQuoteAction(props.quote_id, QuoteAction.save)}

                                className="btn-primary"
                                style={{ whiteSpace: 'nowrap' }}
                            >
                                Download PDF
                            </Button>
                        </ButtonGroup>
                    </div>
                )
            } else {
                buttonsToRender = (<div className="d-flex justify-content-center">

                        <ButtonGroup className=" btn-group-sm">
                            <Button
                                onClick={() => handleQuoteAction(props.quote_id, QuoteAction.save)}
                                className="btn-primary"
                                style={{ whiteSpace: 'nowrap' }}
                            >
                                Download PDF
                            </Button>
                        </ButtonGroup>
                    </div>
                )
            }

            break;

        default:
            buttonsToRender = (
                <div className="d-flex justify-content-center">
                    <ButtonGroup className=" btn-group-sm">
                        <Button
                            onClick={() => handleQuoteAction(props.quote_id, QuoteAction.save)}
                            className="btn-primary"
                            style={{ whiteSpace: 'nowrap' }}
                        >
                            Download PDF
                        </Button>
                    </ButtonGroup>
                </div>
            )
    }
    return (
        <>
            {isLoading
                ? <center><Spinner animation="border" role="status"><span className="visually-hidden">Loading...</span></Spinner></center>
                : buttonsToRender}
            <ActionErrorModal
                show={errorMessages.length > 0}
                onHide={() => setErrorMessages([])}
                messages={errorMessages}
            />
        </>
    )
}


export default QuoteActionButtons;